import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, Clock, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import { SERVICES } from "@/lib/constants";
import { ContractorCard } from "@/components/common";
import AvailabilityPicker, { slotLabel } from "@/components/AvailabilityPicker";
import { LocateMe } from "@/components/LocateMe";
import { useSeo } from "@/lib/seo";

const HAZARDS = ["fire", "smoke", "gas", "wiring", "flood", "other"];
const ORDER = [1, 2, 4, 5, 6, 7, 8];
const TOTAL = ORDER.length;
const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-sm text-slate-700">{label}</Label>{children}</div>;

function YesNo({ value, onChange, id }) {
  const { t } = useI18n();
  return (
    <div className="flex gap-2">
      {[true, false].map((v) => (
        <button type="button" key={String(v)} onClick={() => onChange(v)} data-testid={`${id}-${v ? "yes" : "no"}`}
          className={`px-5 py-2 rounded-full border text-sm font-medium transition-colors ${value === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:border-slate-500"}`}>
          {t(v ? "yes" : "no")}
        </button>
      ))}
    </div>
  );
}

function JobRecs({ req, onPicked }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState(null);
  const select = async (cid) => {
    setBusy(true);
    try { await api.post(`/requests/${req.id}/select`, { contractor_id: cid }); setChosen(cid); toast.success(t("d.chosen") + " ✓"); onPicked && onPicked(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const recs = req.recommended_profiles || [];
  if (!recs.length) return <p className="text-slate-500 mt-2" data-testid="recommendations-none">{t("d.recs_none")}</p>;
  return (
    <>
      <p className="text-slate-500 mt-1">{t(recs.length === 1 ? "d.recs_d1" : "d.recs_d", { service: t(`svc.${req.service}`), city: req.city })}</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid={`recommendations-list-${req.number}`}>
        {recs.map((c) => <ContractorCard key={c.id} c={c} chosen={chosen === c.id} onSelect={chosen ? null : select} busy={busy} />)}
      </div>
    </>
  );
}

function Confirmation({ res }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const reqs = res.requests;
  const first = reqs[0];
  const multi = reqs.length > 1;
  const combined = multi ? first.combined_profiles || [] : [];
  const selectAll = async (cid) => {
    setBusy(true);
    try { await api.post(`/requests/group/${res.group_id}/select`, { contractor_id: cid }); nav("/dashboard"); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="max-w-4xl mx-auto px-4 py-14" data-testid="request-confirmation">
      <div className="rounded-3xl bg-white border border-teal-200 p-8 text-center">
        <CheckCircle2 className="h-12 w-12 text-teal-600 mx-auto" />
        <h1 className="mt-4 text-3xl font-extrabold">{t("conf.title")}</h1>
        <p className="mt-2 text-sm text-slate-500">{t("conf.number")}</p>
        <p className="font-mono text-2xl font-semibold text-teal-700" data-testid="request-number">{multi ? res.number : first.number}</p>
        {multi && <p className="text-sm font-semibold text-slate-700 mt-1" data-testid="jobs-created">{t("conf.jobs", { n: reqs.length })}: {reqs.map((r) => t(`svc.${r.service}`)).join(" · ")}</p>}
        <p className="mt-3 text-slate-600 max-w-lg mx-auto">{t("conf.body")}</p>
        {first.emergency_flag && <p className="mt-4 text-sm text-red-700 bg-red-50 rounded-xl p-3">{t("req.emergency")}</p>}
      </div>
      {combined.length > 0 && (
        <section className="mt-10 rounded-3xl bg-teal-700 p-6 md:p-8" data-testid="combined-recommendations">
          <h2 className="text-2xl font-bold text-white">{t("conf.combined_t")}</h2>
          <p className="text-teal-50/90 mt-1">{t("conf.combined_d", { city: first.city })}</p>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">{combined.map((c) => <ContractorCard key={c.id} c={c} busy={busy} onSelect={selectAll} />)}</div>
        </section>
      )}
      <h2 className="mt-10 text-2xl font-bold">{multi && combined.length > 0 ? t("conf.per_job") : t("d.recs_t")}</h2>
      {reqs.map((r) => (
        <section key={r.id} className="mt-6" data-testid={`job-section-${r.number}`}>
          {multi && <p className="font-semibold text-slate-900">{t(`svc.${r.service}`)} <span className="font-mono text-xs text-slate-400">{r.number}</span></p>}
          <JobRecs req={r} onPicked={multi ? null : () => nav(`/dashboard/requests/${r.id}`)} />
        </section>
      ))}
      <Button variant="outline" className="mt-8 rounded-full" onClick={() => nav(multi ? "/dashboard" : `/dashboard/requests/${first.id}`)} data-testid="open-request-btn">{multi ? t("d.my_requests") : t("conf.view")}</Button>
    </div>
  );
}

export default function RequestService() {
  const { t, lang } = useI18n();
  const { user, signIn } = useAuth();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(1);
  const key = ORDER[step - 1];
  useSeo(t("req.title"), t("req.not_appt"));
  const [cfg, setCfg] = useState({ categories: SERVICES.map((s) => s.key), areas: [] });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [acct, setAcct] = useState({ email: "", password: "" });
  const [f, setF] = useState({
    services: sp.get("service") ? [sp.get("service")] : [], description: "", started: "", danger: false, hazards: [], safe_to_remain: true,
    city: sp.get("city") || "", neighborhood: "", directions: "", access: "", geo: null, preferred_times: "", available_24h: false, customer_urgent: sp.get("urgent") === "1", slots: [],
    contact_name: "", contact_phone: "", contact_method: "whatsapp", consent: false,
  });
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)); }, []);
  useEffect(() => {
    if (user) setF((p) => ({ ...p, contact_name: p.contact_name || user.name, contact_phone: p.contact_phone || user.phone || "" }));
  }, [user]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const emergency = f.danger || f.hazards.length > 0 || !f.safe_to_remain;
  const area = cfg.areas.find((a) => a.name.toLowerCase() === f.city.trim().toLowerCase());

  const slotText = f.slots.map((s) => slotLabel(s, lang, t)).join("; ");
  const sortedServices = SERVICES.filter((s) => cfg.categories.includes(s.key))
    .sort((a, b) => (a.key === "other") - (b.key === "other") || t(`svc.${a.key}`).localeCompare(t(`svc.${b.key}`), lang));

  const valid = () => {
    if (key === 1) return f.services.length > 0;
    if (key === 2) return f.description.trim().length > 0;
    if (key === 4) return f.city.trim().length > 0;
    if (key === 7) return f.contact_name && f.contact_phone && (user || (acct.email && acct.password.length >= 6));
    if (key === 8) return f.consent;
    return true;
  };
  const next = () => { if (valid()) { setStep(step + 1); window.scrollTo(0, 0); } else toast.error(t("req.required")); };

  const submit = async () => {
    if (!valid()) { toast.error(t("req.required")); return; }
    setBusy(true);
    try {
      if (!user) {
        const r = await api.post("/auth/register", { name: f.contact_name, email: acct.email, password: acct.password, phone: f.contact_phone });
        signIn(r.data);
      }
      const { slots, ...rest } = f;
      const r = await api.post("/requests", { ...rest, preferred_times: [slotText, f.preferred_times].filter(Boolean).join(" — ") });
      for (const job of r.data.requests) {
        for (const file of files) {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("stage", "request");
          await api.post(`/requests/${job.id}/files`, fd).catch((e) => toast.error(errMsg(e)));
        }
      }
      setCreated(r.data);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (created) return <Confirmation res={created} />;

  const body = {
    1: (
      <div className="space-y-6">
      <div className={`rounded-2xl border-2 p-4 ${f.customer_urgent ? "border-red-300 bg-red-50" : "border-slate-200"}`} data-testid="req-emergency-ask">
        <p className="font-semibold text-slate-900 mb-3">{t("emerg.q")}</p>
        <YesNo id="req-emergency" value={f.customer_urgent} onChange={(v) => setF({ ...f, customer_urgent: v })} />
        {f.customer_urgent && <p className="mt-3 text-sm text-red-700 flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{t("emerg.yes")}</p>}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sortedServices.map(({ key, icon: Icon }) => (
          <button type="button" key={key} data-testid={`req-service-${key}`} aria-pressed={f.services.includes(key)}
            onClick={() => setF({ ...f, services: f.services.includes(key) ? f.services.filter((x) => x !== key) : [...f.services, key] })}
            className={`flex items-center gap-3 rounded-2xl border p-4 text-start transition-colors ${f.services.includes(key) ? "border-teal-600 bg-teal-50 ring-2 ring-teal-100" : "border-slate-200 bg-white hover:border-slate-400"}`}>
            <Icon className="h-5 w-5 text-teal-700" /><span className="font-medium">{t(`svc.${key}`)}</span>
          </button>
        ))}
      </div>
      </div>
    ),
    2: (
      <div className="space-y-4">
        <F label={`${t("req.what")} *`}><Textarea rows={5} value={f.description} onChange={set("description")} data-testid="req-description-input" /></F>
        <F label={t("req.when")}><Input value={f.started} onChange={set("started")} data-testid="req-started-input" /></F>
      </div>
    ),
    3: (
      <div className="space-y-6">
        <F label={t("req.danger_q")}><YesNo id="req-danger" value={f.danger} onChange={(v) => setF({ ...f, danger: v })} /></F>
        <div>
          <Label className="text-sm text-slate-700">{t("req.hazard_q")}</Label>
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            {HAZARDS.map((h) => (
              <label key={h} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm cursor-pointer">
                <Checkbox checked={f.hazards.includes(h)} data-testid={`req-hazard-${h}`}
                  onCheckedChange={() => setF({ ...f, hazards: f.hazards.includes(h) ? f.hazards.filter((x) => x !== h) : [...f.hazards, h] })} />{t(`hz.${h}`)}
              </label>
            ))}
          </div>
        </div>
        <F label={t("req.safe_q")}><YesNo id="req-safe" value={f.safe_to_remain} onChange={(v) => setF({ ...f, safe_to_remain: v })} /></F>
        {emergency && <div className="flex gap-3 rounded-2xl bg-red-50 border-2 border-red-200 p-4 text-sm text-red-800" data-testid="req-emergency-warning"><AlertTriangle className="h-5 w-5 shrink-0" />{t("req.emergency")}</div>}
      </div>
    ),
    4: (
      <div className="grid sm:grid-cols-2 gap-4">
        <LocateMe geo={f.geo} onFound={({ geo, city, neighborhood }) => setF((p) => ({ ...p, geo, city: p.city || city, neighborhood: p.neighborhood || neighborhood }))} />
        <F label={`${t("f.city")} *`}>
          <Input value={f.city} onChange={set("city")} list="req-areas" data-testid="req-city-input" />
          <datalist id="req-areas">{cfg.areas.map((a) => <option key={a.name} value={a.name} />)}</datalist>
        </F>
        <F label={t("req.neighborhood")}><Input value={f.neighborhood} onChange={set("neighborhood")} data-testid="req-neighborhood-input" /></F>
        <div className="sm:col-span-2"><F label={t("req.directions")}><Textarea rows={2} value={f.directions} onChange={set("directions")} data-testid="req-directions-input" /></F></div>
        <div className="sm:col-span-2"><F label={t("req.access")}><Input value={f.access} onChange={set("access")} data-testid="req-access-input" /></F></div>
        {f.city.trim() && (
          <p className="sm:col-span-2 flex gap-2 text-sm rounded-xl bg-teal-50 text-teal-900 p-3" data-testid="req-area-note">
            <Clock className="h-4 w-4 shrink-0 mt-0.5" />{t(area ? (area.support_247 ? "area.yes247" : area.support_24h ? "area.yes24" : "area.yes") : "area.no", { city: f.city })}
          </p>
        )}
      </div>
    ),
    5: (
      <div className="space-y-4">
        <p className="text-sm text-amber-800 bg-amber-50 rounded-xl p-3">{t("req.photos_note")}</p>
        <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 cursor-pointer hover:border-teal-500">
          <ImagePlus className="h-8 w-8 text-teal-600" /><span className="text-sm font-medium">{t("req.add_files")}</span>
          <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e) => setFiles([...files, ...e.target.files].slice(0, 6))} data-testid="req-files-input" />
        </label>
        {files.length > 0 && <ul className="text-sm text-slate-600 space-y-1" data-testid="req-files-list">{files.map((x, i) => <li key={i}>• {x.name}</li>)}</ul>}
      </div>
    ),
    6: (
      <div className="space-y-5">
        <AvailabilityPicker slots={f.slots} onChange={(slots) => setF({ ...f, slots })} />
        <F label={t("d.notes")}><Textarea rows={2} value={f.preferred_times} onChange={set("preferred_times")} data-testid="req-times-input" /></F>
        <div className="grid sm:grid-cols-2 gap-2">
          <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm cursor-pointer"><Checkbox checked={f.available_24h} onCheckedChange={(v) => setF({ ...f, available_24h: !!v })} data-testid="req-avail24-checkbox" />{t("req.avail24")}</label>
          <label className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold cursor-pointer ${f.customer_urgent ? "border-red-300 bg-red-50 text-red-700" : "border-slate-200"}`}><Checkbox checked={f.customer_urgent} onCheckedChange={(v) => setF({ ...f, customer_urgent: !!v })} data-testid="req-urgent-checkbox" /><AlertTriangle className="h-4 w-4" />{t("req.urgent")}</label>
        </div>
      </div>
    ),
    7: (
      <div className="grid sm:grid-cols-2 gap-4">
        <F label={`${t("f.name")} *`}><Input value={f.contact_name} onChange={set("contact_name")} data-testid="req-contact-name-input" /></F>
        <F label={`${t("f.phone")} *`}><Input value={f.contact_phone} onChange={set("contact_phone")} data-testid="req-contact-phone-input" /></F>
        <div className="sm:col-span-2">
          <Label className="text-sm text-slate-700">{t("req.method")}</Label>
          <div className="flex gap-2 mt-2 flex-wrap">
            {["whatsapp", "phone", "email"].map((m) => (
              <button type="button" key={m} onClick={() => setF({ ...f, contact_method: m })} data-testid={`req-method-${m}`}
                className={`px-4 py-2 rounded-full border text-sm ${f.contact_method === m ? "bg-teal-600 text-white border-teal-600" : "bg-white border-slate-300"}`}>{t(`m.${m}`)}</button>
            ))}
          </div>
        </div>
        {!user && (
          <div className="sm:col-span-2 rounded-2xl bg-slate-50 border border-slate-200 p-4 grid sm:grid-cols-2 gap-4">
            <p className="sm:col-span-2 font-semibold text-sm">{t("req.account")}</p>
            <F label={`${t("f.email")} *`}><Input type="email" value={acct.email} onChange={(e) => setAcct({ ...acct, email: e.target.value })} data-testid="req-account-email-input" /></F>
            <F label={`${t("f.password")} *`}><Input type="password" value={acct.password} onChange={(e) => setAcct({ ...acct, password: e.target.value })} data-testid="req-account-password-input" /></F>
            <Link to="/login?next=/request" className="sm:col-span-2 text-sm text-teal-700 hover:underline" data-testid="req-login-link">{t("req.have_account")}</Link>
          </div>
        )}
      </div>
    ),
    8: (
      <div className="space-y-4" data-testid="req-review">
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm rounded-2xl bg-slate-50 p-5">
          {[["req.s1", f.services.map((s) => t(`svc.${s}`)).join(", ")], ["req.what", f.description], ["f.city", `${f.city}${f.neighborhood ? ", " + f.neighborhood : ""}`],
            ["req.times", [slotText, f.preferred_times].filter(Boolean).join(" — ") || "—"], ["f.name", f.contact_name], ["f.phone", `${f.contact_phone} · ${t(`m.${f.contact_method}`)}`],
            ["req.s5", String(files.length)]].map(([k, v]) => (
            <div key={k}><dt className="text-slate-500 text-xs">{t(k)}</dt><dd className="font-medium text-slate-900 whitespace-pre-wrap">{v}</dd></div>
          ))}
        </dl>
        {emergency && <p className="text-sm text-red-700 font-semibold">{t("req.emergency")}</p>}
        <label className="flex items-start gap-2 text-sm"><Checkbox checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })} data-testid="req-consent-checkbox" className="mt-0.5" />{t("req.consent")}</label>
        <p className="text-sm rounded-xl bg-amber-50 text-amber-900 p-3" data-testid="req-not-appointment">{t("req.not_appt")}</p>
      </div>
    ),
  };

  return (
    <div className="max-w-3xl mx-auto px-4 pt-12 pb-28" data-testid="request-page">
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">{t("req.title")}</h1>
      <div className="mt-6 flex items-center justify-between text-sm">
        <span className="font-semibold text-teal-700" data-testid="req-step-label">{t(`req.s${key}`)}</span>
        <span className="font-mono text-slate-500">{t("req.step", { n: step, total: TOTAL })}</span>
      </div>
      <Progress value={(step / TOTAL) * 100} className="mt-2 h-2" />
      <div className="mt-8 rounded-3xl bg-white border border-slate-200 p-6 md:p-8 fade-up" key={step}>
        {key === 1 && (
          <div className="flex items-center justify-between mb-4 gap-3">
            <p className="font-semibold">{t("req.choose_multi")}</p>
            {f.services.length > 0 && <span className="text-xs font-semibold rounded-full bg-teal-600 text-white px-3 py-1 shrink-0" data-testid="req-selected-count">{t("req.selected", { n: f.services.length })}</span>}
          </div>
        )}
        {body[key]}
      </div>
      <div className="mt-6 flex justify-between">
        <Button variant="outline" className="rounded-full" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="req-back-btn">{t("req.back")}</Button>
        {step < TOTAL ? (
          <Button className="rounded-full bg-teal-600 hover:bg-teal-700 px-8" onClick={next} data-testid="req-next-btn">{t("req.next")}</Button>
        ) : (
          <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8" disabled={busy} onClick={submit} data-testid="req-submit-btn">
            {busy && <Loader2 className="h-4 w-4 me-2 animate-spin" />}{t("req.submit")}
          </Button>
        )}
      </div>
    </div>
  );
}
